import { useConfig } from "@/core/config/ConfigContext";
import { useStoreGroupQuery } from "../api/useStoreGroupsApi";
import { useQuery } from "@tanstack/react-query";
import { ReadStoreGroup } from "@/types/storeGroup";
import { LegacyStoreGroupService } from "../service";
import { API_QUERY_STALE_TIME, Module } from "@/constants";

/**
 * Wrapper hook for fetching a single store group by ID.
 * Encapsulates feature flag logic - components never see it.
 *
 * - FF enabled: Uses new StoreGroupService via useStoreGroupQuery (Phase 2)
 * - FF disabled: Uses legacy APIService singleton
 *
 * @example
 * const { data, isLoading, error } = useStoreGroup(groupId);
 */
export function useStoreGroup(groupId: string) {
  const config = useConfig();
  const useNewService = config.isFeatureEnabled("newStoreGroupServiceEnabled");

  const newServiceQuery = useStoreGroupQuery(groupId, useNewService);

  const legacyQuery = useQuery<ReadStoreGroup>({
    queryKey: [Module.StoreGroups, groupId],
    queryFn: async () => {
      const result = await LegacyStoreGroupService.get(groupId);
      return result as ReadStoreGroup;
    },
    staleTime: API_QUERY_STALE_TIME,
    enabled: !useNewService && Boolean(groupId),
  });

  return useNewService ? newServiceQuery : legacyQuery;
}
